import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import styles from './AirwayAnesthesiaForm.module.scss';
import Dropdown from "../../../../components/dropdown/Dropdown";
import Checkbox from "../../../../components/checkbox/Checkbox";
import RadioButtonGroup from "../../../../components/radioButtonGroup/RadioButtonGroup";
import PropTypes from "prop-types";

const AirwayAnesthesiaForm = forwardRef(({
  formData,
  trachealIntubationList = [],
  regionalAnesthesiaAdministrationList = [],
  numberOfBlocksTotalOnCaseList = [],
  onSave,
  disabled = false
}, ref) => {
  const [trachealIntubation, setTrachealIntubation] = useState('');
  const [trachealIntubationError, setTrachealIntubationError] = useState('');
  const [regionalAnesthesiaAdministration, setRegionalAnesthesiaAdministration] = useState('');
  const [regionalAnesthesiaAdministrationError, setRegionalAnesthesiaAdministrationError] = useState('');
  const [numberOfBlocksTotalOnCase, setNumberOfBlocksTotalOnCase] = useState(null);
  const [numberOfBlocksTotalOnCaseError, setNumberOfBlocksTotalOnCaseError] = useState('');
  const [regionalManagementOnly, setRegionalManagementOnly] = useState(false);
  const [regionalSimulationExperience, setRegionalSimulationExperience] = useState(false);
  const [presentForEmergence, setPresentForEmergence] = useState(null);
  const [presentForEmergenceError, setPresentForEmergenceError] = useState('');

  useEffect(() => {
    setTrachealIntubation(formData?.trachealIntubation || null);
    setRegionalAnesthesiaAdministration(formData?.regionalAnesthesiaAdministration || null);
    setNumberOfBlocksTotalOnCase(formData?.numberOfBlocksTotalOnCase);
    setRegionalManagementOnly(formData?.regionalManagementOnly);
    setRegionalSimulationExperience(formData?.regionalSimulationExperience);
    setPresentForEmergence(formData?.presentForEmergence);
  }, [formData]);

  const validateTrachealIntubation = () => {
    if(!trachealIntubation) {
      return 'Tracheal Intubation is required';
    }
    return '';
  }

  const validateRegionalAnesthesiaAdministration = () => {
    if(!regionalAnesthesiaAdministration) {
      return 'Regional Anesthesia Administration is required';
    }
    return '';
  }

  const validateNumberOfBlocksTotalOnCase = () => {
    if(numberOfBlocksTotalOnCase === null) {
      return 'Number of Blocks Total on Case is required';
    }
    return '';
  }

  const validatePresentForEmergence = () => {
    if(presentForEmergence === null) {
      return 'Present for Emergence is required';
    }
    return '';
  }

  const validateForm = () => {
    const trachealIntubationValidationResult = validateTrachealIntubation();
    const regionalAnesthesiaAdministrationValidationResult = validateRegionalAnesthesiaAdministration();
    const numberOfBlocksTotalOnCaseValidationResult = validateNumberOfBlocksTotalOnCase();
    const presentForEmergenceErrorValidationResult = validatePresentForEmergence();

    setTrachealIntubationError(trachealIntubationValidationResult);
    setRegionalAnesthesiaAdministrationError(regionalAnesthesiaAdministrationValidationResult);
    setNumberOfBlocksTotalOnCaseError(numberOfBlocksTotalOnCaseValidationResult);
    setPresentForEmergenceError(presentForEmergenceErrorValidationResult);
    
    const errors = [
      trachealIntubationValidationResult,
      regionalAnesthesiaAdministrationValidationResult,
      numberOfBlocksTotalOnCaseValidationResult,
      presentForEmergenceErrorValidationResult
    ];
  
    const errorCount = errors.filter(error => error !== '').length;
  
    return errorCount;
  }

  // Expose the function to the parent
  useImperativeHandle(ref, () => ({
    validateForm
  }));

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className={styles.row}>
        <div className={styles.column30}>
          <Dropdown
            label="Tracheal Intubation"
            options={trachealIntubationList}
            value={trachealIntubation}
            onChange={(value) => {
              setTrachealIntubation(value)
              onSave({
                trachealIntubation: value
              })
            }}
            error={trachealIntubationError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column40}>
          <Dropdown
            label="Regional Anesthesia Administration"
            options={regionalAnesthesiaAdministrationList}
            value={regionalAnesthesiaAdministration}
            onChange={(value) => {
              setRegionalAnesthesiaAdministration(value);
              onSave(value === 'Not Applicable (N/A)' ? {
                regionalAnesthesiaAdministration: value,
                numberOfBlocksTotalOnCase: 0
              } : {
                regionalAnesthesiaAdministration: value
              });
            }}
            error={regionalAnesthesiaAdministrationError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <Dropdown
            label="Number of Blocks Total on Case"
            options={numberOfBlocksTotalOnCaseList}
            value={numberOfBlocksTotalOnCase}
            onChange={(value) => {
              setNumberOfBlocksTotalOnCase(value);
              onSave({
                numberOfBlocksTotalOnCase: value
              });
            }}
            error={numberOfBlocksTotalOnCaseError}
            disabled={disabled || regionalAnesthesiaAdministration === 'Not Applicable (N/A)'}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column30}>
          <Checkbox
            label="Regional Management Only"
            checked={regionalManagementOnly}
            onChange={() => {
              const value = !regionalManagementOnly
              setRegionalManagementOnly(value);
              onSave({
                regionalManagementOnly: value
              })
            }}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <Checkbox
            label="Regional Simulation Experience"
            checked={regionalSimulationExperience}
            onChange={() => {
              const value = !regionalSimulationExperience
              setRegionalSimulationExperience(value);
              onSave({
                regionalSimulationExperience: value
              })
            }}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column20}>
          <RadioButtonGroup
            label="Present for Emergence"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={presentForEmergence}
            onChange={(value) => {
              setPresentForEmergence(value)
              onSave({
                presentForEmergence: value
              })
            }}
            error={presentForEmergenceError}
            disabled={disabled}
          />
        </div>
      </div>
    </form>
  );
});

AirwayAnesthesiaForm.displayName = 'AirwayAnesthesiaForm';

AirwayAnesthesiaForm.propTypes = {
  formData: PropTypes.object,
  trachealIntubationList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      subOptions: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired,
        })
      )
    })
  ).isRequired,
  regionalAnesthesiaAdministrationList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      subOptions: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired,
        })
      )
    })
  ).isRequired,
  numberOfBlocksTotalOnCaseList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ).isRequired,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default AirwayAnesthesiaForm;
